import {
  SETORES_HOSPITALARES_PERMISSION_KEY,
  SETORES_HOSPITALARES_SUBMODULE_KEY,
} from '@core/constants/setores-hospitalares-menu.constants';

/**
 * US #61577: the icon name of the *Vacinas* menu group, declared here and READ — not
 * repeated — by the two ends that can read it: `BACKEND_ICON_TO_LUCIDE` below, through a
 * computed key, and `VaccinesMenuSeedMigrationTests` in the Vacinas backend suite, which
 * parses this very declaration to check the migration's MERGE.
 *
 * The third end cannot read it: `LucideAngularModule.pick` in `layout/layout.module.ts`
 * registers the imported `Syringe` SYMBOL, not a name, so nothing ties it to this string
 * at compile time. What ties it is `layout/vaccines-menu-icon.spec.ts`, which renders the
 * name this constant resolves to and fails if the registration does not answer it.
 *
 * Seeding a name the frontend does not register is not a cosmetic slip: the unmapped key
 * falls through `backendIconToLucide` unchanged and reaches `<lucide-icon>` as-is, and
 * `lucide-angular` THROWS for a name no provider knows — the whole menu render goes with
 * it. Reading the constant instead of repeating the literal is also what lets the
 * `syringe` -> `stethoscope` swap foreseen in the plan ([D-20]) happen in one edit.
 */
export const VACCINES_MENU_ICON = 'syringe';

/**
 * Task 62914: the icon name of the *Faturamento SUS* sheet (key `faturamento_sus`), declared
 * here and READ — not repeated — by `BACKEND_ICON_TO_LUCIDE` below (computed key), by the
 * `getIconSvg` allowlist of `QuickAccessConfigModalComponent` (left out of it, the quick-access
 * config modal shows the sheet as `file-text`), and by `SusBillingMenuIconMigrationTests` in the
 * Financeiro backend suite, which parses this declaration to check the UPDATE of the migration
 * that writes it. The last end, `LucideAngularModule.pick` in `layout/layout.module.ts`,
 * registers the `ReceiptText` SYMBOL; `layout/sus-billing-menu-icon.spec.ts` ties it to this
 * name. Same trap as VACCINES_MENU_ICON above: a seeded name nobody registers makes
 * `lucide-angular` THROW and takes the whole menu down.
 */
export const SUS_BILLING_MENU_ICON = 'receipt-text';

/**
 * US #62981 (AC13): the icon name of the *Central de atendimento* leaf (key `central_atendimento`, menu
 * Recepção), declared here and READ — not repeated — by `BACKEND_ICON_TO_LUCIDE` below (computed key), by
 * the `getIconSvg` allowlist of `QuickAccessConfigModalComponent`, and by `AttendanceCenterMenuSeedMigrationTests`
 * in the Atendimento backend suite, which parses this declaration to check the MERGE of the menu seed. The
 * last end, `LucideAngularModule.pick` in `layout/layout.module.ts`, registers the `ContactRound` SYMBOL;
 * `layout/attendance-center-menu-icon.spec.ts` ties it to this name. Same trap as VACCINES_MENU_ICON: a seeded
 * name nobody registers makes `lucide-angular` THROW and takes the whole menu down.
 */
export const ATTENDANCE_CENTER_MENU_ICON = 'contact-round';

/**
 * Mapeamento de ícones retornados pelo backend para nomes Lucide Icons.
 * Suporta tanto nomes antigos (ex.: water, cog) quanto Lucide já usados no back (ex.: home, droplet).
 *
 * Exportado para que a entrada de um ícone possa ser aferida NO MAPA. Aferi-la pela saída de
 * `backendIconToLucide` não distingue nada quando a chave e o valor coincidem: o fallback de
 * passagem devolve a própria chave, então o esperado é o resultado padrão e apagar a entrada
 * não reprova.
 */
export const BACKEND_ICON_TO_LUCIDE: Record<string, string> = {
  home: 'home',
  water: 'droplet',
  droplet: 'droplet',
  cog: 'settings',
  settings: 'settings',
  tag: 'badge-dollar-sign',
  'badge-dollar-sign': 'badge-dollar-sign',
  'account-group': 'users',
  'account-heart': 'users',
  users: 'users',
  'clipboard-list': 'test-tube',
  'test-tube': 'test-tube',
  pill: 'pill',
  stethoscope: 'stethoscope',
  'currency-usd': 'dollar-sign',
  'dollar-sign': 'dollar-sign',
  'package-variant': 'boxes',
  boxes: 'boxes',
  sync: 'arrow-down-up',
  'arrow-down-up': 'arrow-down-up',
  'check-circle': 'search-check',
  'search-check': 'search-check',
  alert: 'siren',
  siren: 'siren',
  link: 'microscope',
  microscope: 'microscope',
  'file-document': 'file-text',
  'file-text': 'file-text',
  forum: 'message-square',
  'message-square': 'message-square',
  'chart-bar': 'chart-column-increasing',
  'chart-column-increasing': 'chart-column-increasing',
  // chart-area / chart-line are not registered in LucideAngularModule (layout.module.ts);
  // we route them to layout-dashboard, which is registered and semantically fits the
  // "Dashboards" folder used in the menu.
  'chart-line': 'layout-dashboard',
  'chart-area': 'layout-dashboard',
  'layout-dashboard': 'layout-dashboard',
  desk: 'users',
  folder: 'file-text',
  user: 'user',
  printer: 'printer',
  'tablet-smartphone': 'tablet-smartphone',
  'shield-check': 'shield-check',
  calculator: 'calculator',
  rss: 'rss',
  'file-check': 'file-check',
  brain: 'brain',
  sparkles: 'sparkles',
  // TASK-60341: Cadastros group (folder-open) + Estabelecimento de Saúde (building-2).
  // hospital/beaker/building also ship in the AutolacMigrations seed for this
  // group — map them explicitly instead of relying on the pass-through fallback.
  'folder-open': 'folder-open',
  'building-2': 'building-2',
  hospital: 'hospital',
  beaker: 'beaker',
  building: 'building',
  // US #61577: the Vacinas group icon. Mapped EXPLICITLY rather than left to the
  // pass-through fallback below, for the same reason the TASK-60341 entries above
  // are — an unmapped name reaches `<lucide-icon>` unchanged, and the library THROWS
  // for a name nobody registered, taking the whole menu render down with it.
  // Computed key, so the name lives in ONE declaration: see VACCINES_MENU_ICON above.
  [VACCINES_MENU_ICON]: VACCINES_MENU_ICON,
  // Task 62914: the Faturamento SUS sheet icon — same reason, see SUS_BILLING_MENU_ICON above.
  [SUS_BILLING_MENU_ICON]: SUS_BILLING_MENU_ICON,
  // US #62981: the Central de atendimento leaf icon — same reason, see ATTENDANCE_CENTER_MENU_ICON above.
  [ATTENDANCE_CENTER_MENU_ICON]: ATTENDANCE_CENTER_MENU_ICON,
};

/**
 * Converte o nome do ícone vindo do backend para o nome Lucide usado no frontend.
 *
 * Nome não mapeado passa INTACTO (fallback de passagem). Isso só é seguro para um nome que o
 * `LucideAngularModule` do layout registre: `lucide-angular` LANÇA
 * `The "<name>" icon has not been provided` para um nome que nenhum provider conhece, e o erro
 * derruba a renderização do menu inteiro. Por isso todo ícone semeado por migration entra
 * explicitamente no mapa acima.
 */
export function backendIconToLucide(backendIcon: string | null | undefined): string {
  if (!backendIcon || !backendIcon.trim()) {
    return 'file-text';
  }
  const key = backendIcon.trim().toLowerCase();
  return BACKEND_ICON_TO_LUCIDE[key] ?? backendIcon;
}

/**
 * Mapeamento de rotas retornadas pelo backend para rotas do Angular (app-routing).
 * Rotas não mapeadas são devolvidas como estão (o app pode ter redirects genéricos).
 *
 * <p>Exportado para que a AUSÊNCIA de uma chave seja asserível (US #61577). Pelo resultado da
 * função não é: ela termina em `?? backendRoute`, então "não está no mapa" e "está no mapa apontando
 * para si mesma" produzem a mesma saída, e uma asserção feita pela função não distingue as duas. É a
 * mesma armadilha que {@link BACKEND_ICON_TO_LUCIDE} já documenta do outro lado.</p>
 */
export const BACKEND_ROUTE_TO_ANGULAR: Record<string, string> = {
  '/inicio': '/home',
  '/home': '/home',
  '/fila-coleta': '/attendance/collection-queue',
  '/cadastro-solicitante': '/attendance/cadastro-solicitante',
  '/novo-atendimento': '/attendance/novo-atendimento',
  '/registro-medicamentos': '/attendance/registro-medicamentos',
  '/atendimento': '/attendance',
  '/custo-exame/painel': '/custo-exame/painel',
  '/custo-exame/equipe': '/custo-exame/equipe',
  '/custo-exame/equipamentos': '/custo-exame/equipamentos',
  '/custo-exame/custos-indiretos': '/custo-exame/custos-indiretos',
  '/custo-exame/exames': '/custo-exame/exames',
  '/custo-exame/calculo-abc': '/custo-exame/calculo-abc',
  '/custo-exame/precificacao': '/custo-exame/precificacao',
  '/custo-exame/simulador': '/custo-exame/simulador',
  '/custo-exame/snapshots': '/custo-exame/snapshots',
  '/pre-atendimento-inteligente': '/pre-attendance',
  '/pre-atendimento': '/pre-attendance',
  // app-routing redirects (legacy routes kept on the backend, with the current
  // FE destination). Without these the flyout leaf does not get highlighted when
  // the user is on the page produced by the redirect — `currentUrl` points to
  // the destination, but `item.route` was pointing to the redirect source.
  '/auditoria-pre-faturamento': '/guide-flow',
  '/cadastro-fluxo-auditoria-pre-faturamento': '/guide-flow-config',
  '/configuracao-impressao': '/printing',
  '/indicadores': '/punctuality-monitoring',
  '/monitoramento-pontualidade-resultados': '/punctuality-monitoring',
  '/gestao-comunicacao-times': '/messages',
  '/mensagens': '/messages',
  // US #62981: the menu seed ships the PT route; the page lives at ATTENDANCE_CENTER_ROUTE_PREFIX.
  '/central-atendimento': '/attendance-center',
  // TASK-60341: Cadastros → Estabelecimento de Saúde was an identity mapping used
  // only to drive the (now removed) SPA route. The listing is an overlay opened
  // by submodule key (`opensAsOverlay`), so no route mapping is needed and
  // `isSpaInternalRoute` no longer references `/health-facilities`.
};

/**
 * TASK-60341 (AC2 / Open Question 3): submodule keys whose sidebar item is
 * gated by a backend permission. The value is the SAME shared access key used
 * by the API endpoint (`UserAccessFilter.For`) and by the route guard —
 * defense in depth with a single source of truth per feature.
 *
 * Keys ABSENT from this map are never checked and stay always visible
 * (zero impact on the current menu). Next modernized cadastros generalize by
 * adding one entry here.
 */
export const HEALTH_FACILITIES_PERMISSION = 'ESTABELECIMENTO_SAUDE';

/**
 * TASK-60341: single source of truth for the Estabelecimento de Saúde submodule
 * key. Referenced by the permission-gating map, the overlay predicate
 * (`opensAsOverlay`) and the sidebar/drawer/home leaf-activation branches — no
 * magic string duplicated across consumers.
 */
export const SUBMODULE_KEY_ESTABELECIMENTO_SAUDE = 'estabelecimento_saude';

/**
 * US #60630: chave de acesso REAPROVEITADA do endpoint já existente em
 * Atendimento (`/api/collection-queues`) — não é uma chave nova (Spec §6).
 */
export const COLLECTION_QUEUES_PERMISSION = 'VISUALIZA_FILA_COLETA';

/**
 * US #60630: single source of truth para a key do submódulo "Filas de Coleta"
 * (cadastro, módulo Cadastros — seed já existente na migration 054,
 * `cadastro_filas_coleta`). NÃO confundir com a tela operacional "Fila de
 * Coleta" (singular, `features/attendance/pages/collection-queue-page`,
 * outra key/guard) — esta é a listagem de cadastro desta US.
 */
export const SUBMODULE_KEY_CADASTRO_FILAS_COLETA = 'cadastro_filas_coleta';

/**
 * TASK-60770 (AC2): access key of the Cadastro de Clínicas — the SAME string the
 * API endpoint requires (`UserAccessFilter.For('CLINICA')`, mapped to legacy
 * position 383 → web 382). Singular, following `ESTABELECIMENTO_SAUDE`: one key
 * for the menu item, the screen and the API.
 */
export const CLINICS_PERMISSION = 'CLINICA';

/**
 * TASK-60770: single source of truth for the "Clínicas" submodule key — seeded
 * by migration 054 under the Cadastros group (`cadastro_clinicas`, route
 * `/clinics`, icon `hospital`). This US changes NO seed.
 */
export const SUBMODULE_KEY_CADASTRO_CLINICAS = 'cadastro_clinicas';

/**
 * US #61577: single source of truth for the "Vacinas → Configuração" submodule key
 * (seeded by AutolacMigrations 202605060080 as `vacinas_configuracao`). Referenced
 * by the overlay predicate (`opensAsOverlay`), the mobile allowlist and the
 * sidebar/drawer/home opener maps — no magic string duplicated across consumers.
 *
 * NOT in `SUBMODULE_KEY_TO_PERMISSION`: the module defines no access key of its
 * own (Spec §7). The `MinimumBusinessAccessMiddleware` on the Host still refuses
 * an authenticated user with no effective web permission — that gate is not opted
 * out of, and needs no entry here.
 */
export const SUBMODULE_KEY_VACINAS_CONFIGURACAO = 'vacinas_configuracao';

/**
 * Access key gating Smart Pre-Attendance: only whoever has attendance released reaches the routine.
 * REUSED key — legacy ACESSO position 358, web 357 (`AtendimentoVisaoGeralDosAtendimentos`), already
 * gating `/api/request-stats`; there is no position of its own for pre-attendance. Gates the
 * `/api/pre-attendance` endpoints and `PreAttendanceGuard`; the sidebar item stays listed, matching
 * the operational Fila de Coleta screen (`fila_coleta`) — see the absence asserted in
 * `SUBMODULE_KEY_TO_PERMISSION`'s spec.
 */
export const PRE_ATTENDANCE_PERMISSION = 'ATENDIMENTO_VISAO_GERAL_ATENDIMENTOS';

/**
 * US #61499: the ONE access key of the Controle de Caixa grid (legacy access item 390 → web
 * position 389). The same string gates the API route (`UserAccessFilter.For`), translates to the
 * position in `UserAccessChecker`'s map, and drives the conditional render on the client.
 */
export const CASH_CONTROL_PERMISSION = 'CONTROLE_CAIXA_GRADE';

/**
 * US #61501: the access key of "Incluir Pagamentos" (legacy access item 183 → web position 182,
 * `PermissionAccess.CaixaIncluirPagamentos`). The same string gates the confirmation route
 * (`UserAccessFilter.For`), resolves to the position in `UserAccessChecker`'s map, and drives the
 * conditional render of the Inserir action of the Modal Pagamentos (D-03).
 *
 * It is NOT an entry of {@link SUBMODULE_KEY_TO_PERMISSION}: that map is keyed by MENU LEAF, and the
 * leaf of the Controle de Caixa is already there under {@link CASH_CONTROL_PERMISSION} (D-12, shipped
 * by #61499). Inserting a payment is an action inside the screen, not a leaf of the menu — added
 * there, it would gate the leaf itself by the inclusion item and take the screen away from the
 * operator CT-1.5 says still sees it.
 */
export const CASH_PAYMENT_INSERT_PERMISSION = 'CAIXA_INCLUIR_PAGAMENTOS';

/**
 * US #61503: the access key of "Alterar Pagamentos" (legacy access item 184 → web position 183,
 * `PermissionAccess.CaixaAlterarPagamentos`). The same string gates the two routes of the alteração
 * (`UserAccessFilter.For`), resolves to the position in `UserAccessChecker`'s map, and drives the
 * conditional render of the Alterar action of the Modal Pagamentos (D-03).
 *
 * NOT position 184: that one is `CaixaExcluirPagamentos`, and reading the legacy number literally
 * would tie the alteração to the permission to DELETE — an error that fails neither at compile time
 * nor at run time.
 *
 * It is NOT an entry of {@link SUBMODULE_KEY_TO_PERMISSION}, for the reason
 * {@link CASH_PAYMENT_INSERT_PERMISSION} gives: altering a payment is an action inside the screen,
 * not a leaf of the menu.
 */
export const CASH_PAYMENT_ALTER_PERMISSION = 'CAIXA_ALTERAR_PAGAMENTOS';

/**
 * US #61504: the access key of "Excluir Pagamentos" (legacy access item 185 → web position 184,
 * `PermissionAccess.CaixaExcluirPagamentos`). The same string gates the deletion route
 * (`UserAccessFilter.For`), resolves to the position in `UserAccessChecker`'s map, and drives the
 * conditional render of the Excluir action of the Modal Pagamentos (D-03).
 *
 * NOT position 185: that one is Executar Relatórios, and reading the legacy number literally would give
 * the deletion to whoever runs reports.
 *
 * It is NOT an entry of {@link SUBMODULE_KEY_TO_PERMISSION}, for the reason
 * {@link CASH_PAYMENT_INSERT_PERMISSION} gives.
 */
export const CASH_PAYMENT_DELETE_PERMISSION = 'CAIXA_EXCLUIR_PAGAMENTOS';

/**
 * US #61499: single source of truth for the "Controle de Caixa" submodule key (menu Faturamento).
 * Referenced by the permission-gating map and the mobile allowlist — no magic string duplicated.
 */
export const SUBMODULE_KEY_CONTROLE_CAIXA = 'controle_caixa';

/**
 * US #61499: the router path of the Controle de Caixa page, shipped by the menu seed alongside the
 * key above (`usuarios/056_MenuFaturamentoControleDeCaixa.sql`). The route in
 * `app-routing.module.ts` and the mobile allowlist read it from here.
 *
 * Declared here, next to the key, because the pair travels together: a client that knows the key
 * and not the prefix leaves the leaf reachable from the drawer and invisible in the home grid. The
 * LITERAL still appears in `mobile-ready-features.spec.ts`, deliberately: the string is a contract
 * with a SQL seed no TypeScript constant can read, so the spec is where it is pinned, and the
 * allowlist reading this constant is what makes that pin bite.
 */
export const CASH_CONTROL_ROUTE_PREFIX = '/cash-control';

/**
 * US #61506: the ONE access key of the Fechamento de Caixa (legacy access item 201 → web position 200,
 * `PermissionAccess.CaixaFechamentoDeCaixa`, D-70). The same string gates the API route
 * (`UserAccessFilter.For` in `CashClosingEndpoints`), resolves to the position in `UserAccessChecker`'s
 * map, draws the Fechamento de Caixa button of the Controle de Caixa action bar and drives the
 * conditional render of the modal.
 *
 * It is NOT a submodule key: the closing has no menu leaf. Its entry point is that button ([OQ-18] of
 * the plan of US #61506, D-102), so no `SUBMODULE_KEY_*` and no entry in {@link SUBMODULE_KEY_TO_PERMISSION}
 * or {@link OVERLAY_SUBMODULE_KEY_LIST} exist for it.
 *
 * NOT position 201: that one is `ImportarExportarBioScan`, and reading the legacy number literally would
 * tie the closing of the caixa to the BioScan import — an error that fails neither at compile time nor
 * at run time.
 */
export const CASH_CLOSING_PERMISSION = 'CAIXA_FECHAMENTO_DE_CAIXA';

/**
 * US #61510: the ONE access key of the reports of the Controle de Caixa — Executar Relatórios, legacy access item 186 →
 * web position 185, `PermissionAccess.CaixaExecutarRelatorios` (D-64, item 3). The same string gates the API routes
 * of the Movimentação do Caixa (`UserAccessFilter.For` in `CashMovementReportEndpoints`), resolves to the position in
 * `UserAccessChecker`'s map and draws the Movimentação button of the Controle de Caixa header. The #61511
 * (Créditos/Débitos) consumes the same key.
 *
 * It is NOT a submodule key: the Movimentação has no menu leaf. Its entry point is that button (D-106, the second
 * application of D-102), so no `SUBMODULE_KEY_*` and no entry in {@link SUBMODULE_KEY_TO_PERMISSION} or
 * {@link OVERLAY_SUBMODULE_KEY_LIST} exist for it.
 *
 * NOT position 186: that value belongs to another domain, and reading the legacy number literally fails neither at
 * compile time nor at run time.
 */
export const CASH_REPORTS_PERMISSION = 'CONTROLE_CAIXA_RELATORIOS';

/**
 * US #61003: the ONE access key of the Setores do Laboratório listing (legacy access item 116 → web
 * position 115, `PermissionAccess.Setores`). NEW key, decided by this US in the pt-BR shape of its
 * neighbours (`ESTABELECIMENTO_SAUDE`, `VISUALIZA_FILA_COLETA`), and ratified for the whole Feature
 * #60949. The same string gates the API route (`UserAccessFilter.For`), resolves to the position in
 * `UserAccessChecker`'s map and drives the conditional render of the menu item.
 */
export const LABORATORY_SECTORS_PERMISSION = 'SETOR_LABORATORIO';

/**
 * US #61003: single source of truth for the "Setores do Laboratório" submodule key (menu Cadastros,
 * seeded by `usuarios/057_MenuSubmoduloSetorLaboratorio.sql`). The `cadastro_` prefix comes from the
 * 054 seed so it cannot collide with anything under Recepção.
 *
 * There is NO entry in `BACKEND_ROUTE_TO_ANGULAR` for `/laboratory-sectors`: the leaf is an overlay,
 * not a route — same as the two modal siblings.
 */
export const SUBMODULE_KEY_CADASTRO_SETORES_LABORATORIO = 'cadastro_setores_laboratorio';

/**
 * US #62189: the ONE access key of the Tabela de Preços registry (legacy access item 26 → web position 25,
 * `PermissionAccess.TabelaDePreco`). The same string gates the API route (`UserAccessFilter.For`), resolves to the
 * position in `UserAccessChecker`'s map and decides whether the menu item shows on the three surfaces.
 */
export const PRICE_TABLES_PERMISSION = 'TABELA_PRECO';

/**
 * US #62189: the "Tabela de Preços" submodule key (menu Cadastros, seeded by
 * `usuarios/069_MenuSubmoduloTabelaPreco.sql`). An overlay leaf: no entry in `BACKEND_ROUTE_TO_ANGULAR`.
 */
export const SUBMODULE_KEY_CADASTRO_TABELA_PRECO = 'cadastro_tabela_preco';

/**
 * US 62835: the "Cadastro de Paciente" submodule key (menu Recepção, seeded by
 * `usuarios/064_RestaurarSubmenuCadastroPacienteRecepcao.sql`). Not gated by permission (D-02).
 */
export const SUBMODULE_KEY_CADASTRO_PACIENTE = 'cadastro_paciente';

/**
 * US #62593: the ONE access key of the Faturamento SUS (legacy access item 54 → web position 53,
 * `PermissionAccess.FaturamentoSUS`). The same string gates the API routes (`UserAccessFilter.For`),
 * resolves to the position in `UserAccessChecker`'s map, hides the menu leaf without it and is checked
 * again by the page before the filters are shown. NOT position 54: that one is "Médicos somente leitura".
 */
export const SUS_BILLING_PERMISSION = 'FATURAMENTO_SUS';

/**
 * US #62593: single source of truth for the "Faturamento SUS" submodule key (menu Faturamento, seeded by
 * `usuarios/063_MenuFaturamentoFaturamentoSus.sql`). Referenced by the permission-gating map — no magic
 * string duplicated.
 */
export const SUBMODULE_KEY_FATURAMENTO_SUS = 'faturamento_sus';

/**
 * US #62800: the ONE access key of Laudos › Impressão de Laudos (legacy access item [32] → web position 31,
 * `PermissionAccess.ImpressaoDeLaudos`). The same string gates the API routes (`UserAccessFilter.For`), resolves
 * to the position in `UserAccessChecker`'s map, hides the menu leaf without it and is checked again by
 * `PrintReportsGuard`. NOT position 32: that one is "Dados do Atendimento".
 */
export const PRINT_REPORTS_PERMISSION = 'IMPRESSAO_DE_LAUDOS';

/**
 * US #62800: single source of truth for the "Impressão de Laudos" submodule key (menu Laudos, seeded by
 * `usuarios/064_MenuLaudosImpressaoDeLaudos.sql` with the route `/printing/laudos/imprimir`). Referenced by the
 * permission-gating map — no magic string duplicated.
 */
export const SUBMODULE_KEY_IMPRESSAO_LAUDOS = 'impressao_laudos';

/**
 * US #62593: the router path of the Faturamento SUS page, shipped by the menu seed alongside the key above
 * (`usuarios/063_MenuFaturamentoFaturamentoSus.sql`). The route in `app-routing.module.ts` and
 * `isSpaInternalRoute` read it from here; it needs no entry in `BACKEND_ROUTE_TO_ANGULAR`, which passes it
 * through unchanged, like `/cash-control`. Declared beside the key because the pair travels together in the seed.
 */
export const SUS_BILLING_ROUTE_PREFIX = '/sus-billing';

/**
 * US #62981: single source of truth for the "Central de atendimento" submodule key (menu Recepção, seeded by
 * `usuarios/067_MenuCentralAtendimentoRecepcao.sql`).
 *
 * NOT in `SUBMODULE_KEY_TO_PERMISSION` and NOT an overlay: the search and the patient record ask for no access
 * key of their own (AC13, RN-07) and the Central is a routed page. The `MinimumBusinessAccessMiddleware` on the
 * Host still refuses an authenticated user with no effective web permission.
 */
export const SUBMODULE_KEY_CENTRAL_ATENDIMENTO = 'central_atendimento';

/**
 * US #62981: the router path of the Central de atendimento. The seed ships the PT route `/central-atendimento`,
 * which `BACKEND_ROUTE_TO_ANGULAR` translates to this path. `isSpaInternalRoute` reads it from here; the
 * translation above and the route in `app-routing.module.ts` (`path: 'attendance-center'`) write it as a literal,
 * and `app-routing.module.spec.ts` confronts the route with this constant.
 */
export const ATTENDANCE_CENTER_ROUTE_PREFIX = '/attendance-center';

export const SUBMODULE_KEY_TO_PERMISSION: Readonly<Record<string, string>> = {
  [SUBMODULE_KEY_ESTABELECIMENTO_SAUDE]: HEALTH_FACILITIES_PERMISSION,
  [SETORES_HOSPITALARES_SUBMODULE_KEY]: SETORES_HOSPITALARES_PERMISSION_KEY,
  [SUBMODULE_KEY_CADASTRO_FILAS_COLETA]: COLLECTION_QUEUES_PERMISSION,
  [SUBMODULE_KEY_CADASTRO_CLINICAS]: CLINICS_PERMISSION,
  [SUBMODULE_KEY_CONTROLE_CAIXA]: CASH_CONTROL_PERMISSION,
  [SUBMODULE_KEY_CADASTRO_SETORES_LABORATORIO]: LABORATORY_SECTORS_PERMISSION,
  [SUBMODULE_KEY_FATURAMENTO_SUS]: SUS_BILLING_PERMISSION,
  [SUBMODULE_KEY_IMPRESSAO_LAUDOS]: PRINT_REPORTS_PERMISSION,
  [SUBMODULE_KEY_CADASTRO_TABELA_PRECO]: PRICE_TABLES_PERMISSION,
};

/**
 * TASK-60770 (Inc 7, AC2): submodule keys whose HOME quick-access tile is gated by
 * permission. It is a SCOPE list, not a second key->permission mapping: which
 * permission guards a key is read from `SUBMODULE_KEY_TO_PERMISSION` above, the
 * same single source of truth the sidebar gate uses, so the two surfaces cannot
 * disagree on WHICH permission a feature needs. What can differ, and is meant to,
 * is WHERE the gate applies.
 *
 * Why not gate every key of `SUBMODULE_KEY_TO_PERMISSION` here: the tiles of the two
 * cadastros already shipped (Estabelecimento de Saúde, Filas de Coleta) render
 * ungated today, and turning them off is a decision of the owners of those stories,
 * not of this one. The source that covers BOTH is spec §6 ("gate ... restrito à chave
 * `cadastro_clinicas`") plus the Out of scope of the plan's Inc 7 ("comportamento dos
 * tiles dos outros cadastros"); spec §7 names only Estabelecimento de Saúde, so it
 * alone does not carry Filas de Coleta.
 * DEBT (plan R-5): generalize to the full map once that decision is taken, and delete
 * this set. Two readers, not one: `quickAccessPermissionFor` just below, and
 * `home-page.component.ts` (`resolveQuickAccessPermissions`), which iterates this set
 * directly to probe each permission once per mount — both have to go with it.
 */
export const QUICK_ACCESS_GATED_SUBMODULE_KEYS: ReadonlySet<string> = new Set<string>([
  SUBMODULE_KEY_CADASTRO_CLINICAS,
  // US #62189 (decision 1): the Tabela de Preços tile is gated by the same key the sidebar and the API use.
  SUBMODULE_KEY_CADASTRO_TABELA_PRECO,
]);

/**
 * Permission required to render the home quick-access tile of `key`, or `undefined`
 * when that tile is not gated — the same shape, and the same fall-through, that the
 * sidebar gate reads straight off `SUBMODULE_KEY_TO_PERMISSION`.
 *
 * A key listed as gated but missing from that map would fall through as ungated. No
 * runtime branch guards against it, because with the constants as they stand the
 * branch would be unreachable and untestable; what guards it is the invariant spec in
 * `backend-menu.util.spec.ts` ("every gated key is mapped in SUBMODULE_KEY_TO_PERMISSION").
 *
 * That invariant guards ONE direction only — a key added HERE and forgotten in the map.
 * The inverse is not guarded: a key added to `SUBMODULE_KEY_TO_PERMISSION` (gating the
 * sidebar) and forgotten in the gated set leaves its tile open, and every spec in that
 * describe still passes. Deliberate, and inside the mitigation the plan contracted for
 * R-5: the tiles ARE ungated by default today, so the omission reproduces the current
 * behaviour rather than regressing it. Registered as debt in the plan's Inc 7 log.
 */
export function quickAccessPermissionFor(key: string | null | undefined): string | undefined {
  if (!key || !QUICK_ACCESS_GATED_SUBMODULE_KEYS.has(key)) {
    return undefined;
  }
  return SUBMODULE_KEY_TO_PERMISSION[key];
}

/**
 * TASK-60341/US #60630: submodule keys whose menu item opens an in-app
 * OVERLAY (modal) instead of navigating to a route. Centralizes the "opens as
 * overlay" decision so every entry point (desktop sidebar, mobile drawer,
 * home quick-access) shares one rule. Currently Estabelecimento de Saúde,
 * Filas de Coleta, Vacinas → Configuração, Setores do Laboratório, Cadastro de
 * Paciente and Tabela de Preços; each entry point's leaf-activation handler must branch on the specific
 * key to open the right launcher/modal service — adding a key here alone is not enough.
 *
 * Declared as a tuple, not a `Set`, because {@link OverlaySubmoduleKey} is derived
 * FROM it: adding a key here is what makes `tsc` demand the matching entry in
 * every surface's opener map. Keep the `as const`.
 */
export const OVERLAY_SUBMODULE_KEY_LIST = [
  SUBMODULE_KEY_ESTABELECIMENTO_SAUDE,
  SUBMODULE_KEY_CADASTRO_FILAS_COLETA,
  SUBMODULE_KEY_CADASTRO_CLINICAS,
  SUBMODULE_KEY_VACINAS_CONFIGURACAO,
  SUBMODULE_KEY_CADASTRO_SETORES_LABORATORIO,
  SUBMODULE_KEY_CADASTRO_PACIENTE,
  SUBMODULE_KEY_CADASTRO_TABELA_PRECO,
] as const;

/**
 * The keys {@link opensAsOverlay} answers true for, as a closed union.
 *
 * Deciding THAT a leaf opens as an overlay lives here; HOW to open it lives in one
 * `overlayOpeners` map per surface (sidebar, drawer, home quick-access), which each
 * surface keeps because the modal services it opens are its own dependencies. Those
 * maps are typed `Readonly<Record<OverlaySubmoduleKey, () => void>>`, so a key added
 * to the list above and forgotten in one of them is a COMPILE error — not a leaf
 * that takes the overlay branch, finds no opener and returns having done nothing,
 * which is a dead menu entry on that one surface with no error anywhere.
 */
export type OverlaySubmoduleKey = (typeof OVERLAY_SUBMODULE_KEY_LIST)[number];

const OVERLAY_SUBMODULE_KEYS: ReadonlySet<string> = new Set<string>(OVERLAY_SUBMODULE_KEY_LIST);

/**
 * True when a menu item identified by its backend `key` must open as an in-app
 * overlay (modal) rather than navigate. Callers branch on this BEFORE any route
 * navigation / external-launch resolution.
 *
 * Narrows to {@link OverlaySubmoduleKey} so the caller can index its opener map
 * directly, without an optional call that would swallow a missing entry.
 */
export function opensAsOverlay(key: string | null | undefined): key is OverlaySubmoduleKey {
  if (!key) return false;
  return OVERLAY_SUBMODULE_KEYS.has(key);
}

/**
 * Modules that the backend currently exposes either as a folder (parent + 1 child)
 * or without a route, but that should behave as a single top-level leaf in the
 * sidebar/quick-access — same UX as Início. Values are the Angular SPA route the
 * leaf should navigate to.
 */
// Keys are stored without diacritics — the lookup also strips them so
// "Comunicação de Times" / "Comunicacao de Times" / "COMUNICAÇÃO DE TIMES"
// all resolve to the same entry without enumerating every variant.
const MODULE_DEFAULT_ROUTE: Record<string, string> = {
  'inicio': '/home',
  'home': '/home',
  'comunicacao': '/messages',
  'team communication': '/messages',
  'comunicacion de equipos': '/messages',
  'comunicacao de times': '/messages',
  'gestao da comunicacao de times': '/messages',
};

/**
 * Returns the default route for a module identified by its display name when
 * the backend doesn't (yet) expose a `route` field for it. Lets us keep menu
 * navigation working through legacy seed states where a parent module groups a
 * single submodule with a known route. Diacritics are stripped before the
 * lookup, so "Comunicação de Times" and "Comunicacao de Times" both match the
 * same key — avoids enumerating every accented/non-accented variant in the map.
 */
export function getDefaultRouteForModuleName(name: string | null | undefined): string | undefined {
  if (!name || !name.trim()) return undefined;
  // ̀–ͯ is the Unicode "Combining Diacritical Marks" block; after
  // NFD decomposition each accented letter splits into base + combining mark,
  // so this regex strips the marks and leaves the bare ASCII form.
  const normalized = name.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  return MODULE_DEFAULT_ROUTE[normalized];
}

/**
 * Converte a rota vinda do backend para a rota usada no Angular (quando houver mapeamento).
 */
export function backendRouteToAngular(backendRoute: string | null | undefined): string {
  if (!backendRoute || !backendRoute.trim()) {
    return '';
  }
  const normalized = backendRoute.trim().toLowerCase();
  return BACKEND_ROUTE_TO_ANGULAR[normalized] ?? backendRoute;
}

/**
 * Rotas que permanecem na SPA Angular (Atendimento e Custo Exame).
 * Qualquer outro item de menu abre o cliente Windows / AVD em nova aba.
 */
export function isSpaInternalRoute(route: string | null | undefined): boolean {
  if (!route || !route.trim()) {
    return false;
  }
  const path = route.trim().split('?')[0].toLowerCase();
  if (path === '/home' || path.startsWith('/home/')) {
    return true;
  }
  if (path === '/attendance' || path.startsWith('/attendance/')) {
    return true;
  }
  if (path === '/custo-exame' || path.startsWith('/custo-exame/')) {
    return true;
  }
  // Other SPA routes (still Angular) — if these are treated as external, the home
  // quick-access opens the AVD client instead of navigating the current app. The
  // quick-access is the ONLY consumer of this list (see the US #61464 note below);
  // the sidebar and the mobile drawer never read it.
  if (path === '/pre-attendance' || path.startsWith('/pre-attendance/')) {
    return true;
  }
  if (path === '/guide-flow' || path.startsWith('/guide-flow/')) {
    return true;
  }
  if (path === '/guide-flow-config' || path.startsWith('/guide-flow-config/')) {
    return true;
  }
  if (path === '/printing' || path.startsWith('/printing/')) {
    return true;
  }
  // The WhatsApp attendance panel.
  if (path === '/whatsapp' || path.startsWith('/whatsapp/')) {
    return true;
  }
  if (path === '/messages' || path.startsWith('/messages/')) {
    return true;
  }
  if (path === '/punctuality-monitoring' || path.startsWith('/punctuality-monitoring/')) {
    return true;
  }
  if (path === '/godmode' || path.startsWith('/godmode/')) {
    return true;
  }
  // Cadastros → Setores Hospitalares is a routed page: without this entry a pinned quick-access card
  // would launch the Windows client instead of navigating.
  if (path === '/hospital-sectors' || path.startsWith('/hospital-sectors/')) {
    return true;
  }
  // US #61464 (AR-08b): the home quick-access is the ONLY consumer of this
  // list — it confronts the card route with it before deciding between an
  // in-app navigation and launching the AVD in a new tab. The sidebar and the
  // mobile drawer never read it. Without this entry a pinned Tasy card would
  // open the Windows client instead of routing to the page (AC-04).
  if (path === '/integrations' || path.startsWith('/integrations/')) {
    return true;
  }
  // Faturamento → Controle de Caixa is a routed page: without this entry a pinned card would
  // launch the Windows client instead of navigating.
  if (path === CASH_CONTROL_ROUTE_PREFIX || path.startsWith(`${CASH_CONTROL_ROUTE_PREFIX}/`)) {
    return true;
  }
  // Faturamento → Faturamento SUS is a routed page (US #62593): without this entry a pinned card would
  // launch the Windows client instead of navigating.
  if (path === SUS_BILLING_ROUTE_PREFIX || path.startsWith(`${SUS_BILLING_ROUTE_PREFIX}/`)) {
    return true;
  }
  // Recepção → Central de atendimento is a routed page (US #62981): without this entry a pinned card would
  // launch the Windows client instead of navigating.
  if (path === ATTENDANCE_CENTER_ROUTE_PREFIX || path.startsWith(`${ATTENDANCE_CENTER_ROUTE_PREFIX}/`)) {
    return true;
  }
  // TASK-60341: Cadastros → Estabelecimento de Saúde is an in-app OVERLAY opened
  // by submodule key (see `opensAsOverlay`), not a route — every consumer
  // branches on the key before route classification, so `/health-facilities`
  // is intentionally NOT an SPA-internal route anymore.
  return false;
}
